import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, resendVerificationEmail, logout, isAuthenticated } = useAuth();
  const { translate } = useLanguage();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Ref-based in-flight lock so the guard doesn't re-render and re-trigger effects.
  const isCheckingRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', {
        state: { message: 'Please log in to verify your email address.' }
      });
    }
  }, [isAuthenticated, navigate]);

  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Stable callback — empty deps. The ref-based guard prevents overlapping
  // calls, and putting state into useCallback deps would rebuild the function
  // on every render and re-trigger the polling effect (infinite /auth/me loop).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkVerificationStatus = useCallback(async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      setIsCheckingStatus(true);
      const response = await api.get('/auth/me');
      const updatedUser = response.data;

      if (updatedUser.verified) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          parsedUser.verified = true;
          localStorage.setItem('user', JSON.stringify(parsedUser));
        }

        window.dispatchEvent(new CustomEvent('auth:userUpdated', {
          detail: { user: updatedUser }
        }));

        setResendMessage('Email verified successfully! Redirecting…');
        setTimeout(() => {
          navigate('/');
        }, 2000);

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    } finally {
      setIsCheckingStatus(false);
      isCheckingRef.current = false;
    }
  }, [navigate]);

  // Start polling only when authenticated and unverified. Depend on the
  // verified flag (not the whole user object, whose reference may churn)
  // so the effect doesn't re-subscribe on every user update.
  useEffect(() => {
    if (!isAuthenticated || !user || user.verified) return;

    checkVerificationStatus();
    pollingIntervalRef.current = setInterval(() => {
      checkVerificationStatus();
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // checkVerificationStatus is stable (empty deps); ignore lint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.verified]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated && user && !user.verified) {
        checkVerificationStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // checkVerificationStatus is stable. user is read inside the handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.verified]);

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendMessage('');
    setResendError('');

    try {
      const response = await resendVerificationEmail();
      setResendMessage(response.message || 'Verification email sent successfully!');
      setTimeout(() => checkVerificationStatus(), 2000);
    } catch (error) {
      setResendError('Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="page-tight" style={{ paddingTop: 48, textAlign: 'center' }}>
      {resendMessage && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="ok">{resendMessage}</Callout>
        </div>
      )}

      {resendError && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">{resendError}</Callout>
        </div>
      )}

      {/* Icon badge */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: 'var(--bg-tint)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
        }}
      >
        {isCheckingStatus
          ? <Icon name="refresh" size={24} />
          : <Icon name="mail" size={24} />
        }
      </div>

      <h1 style={{ fontSize: 28, letterSpacing: '-0.02em', margin: 0 }}>
        {translate('verify-email-address')}
      </h1>

      <p className="muted" style={{ marginTop: 8 }}>
        {translate('verification-email-sent')}{' '}
        <strong style={{ color: 'var(--ink)' }}>{user?.email}</strong>.{' '}
        {translate('verification-instructions')}
      </p>

      {isCheckingStatus && (
        <div style={{ marginTop: 12 }}>
          <Callout tone="info">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Icon name="refresh" size={14} />
              Checking verification status…
            </span>
          </Callout>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleResendVerification}
          disabled={isResending}
        >
          <Icon name="refresh" size={14} />
          {isResending ? (translate('sending') || 'Sending…') : translate('resend-email')}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={handleLogout}
        >
          {translate('use-different-account')}
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
